"use client";

import { Loader2, Sparkles, TriangleAlert } from "lucide-react";
import { cn } from "@/lib/utils";
import type { LucideProps } from "lucide-react";
import React from "react";
import { NoxusTooltip } from "@/components/UI/NoxusTooltip";

interface HeuristicScoreBadgeProps {
  score: number | null; // weighted_score * 10 (0-100)
  isLoading: boolean;
  isDisplay?: boolean;
  isOutdated?: boolean;
  isError?: boolean;
  errorMessage?: string | null;
  onClick?: () => void;
}

export const ScoresEnum = {
  strong: 75,
  fair: 40,
  poor: 0,
} as const;

function getScoreLabel(score: number | null): string {
  if (score === null) return "No score";
  if (score >= 90) return "Excellent";
  if (score >= ScoresEnum.strong) return "Strong";
  if (score >= ScoresEnum.fair) return "Fair";
  return "Poor";
}

const AnimatedSparkles = React.forwardRef<SVGSVGElement, LucideProps>(
  (
    {
      color = "currentColor",
      size = 24,
      strokeWidth = 1.33,
      className,
      ...props
    },
    ref
  ) => (
    <svg
      ref={ref}
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      {...props}
    >
      <g className="sparkles-major">
        <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z" />
      </g>
      <g transform="translate(20 5)">
        <g className="sparkles-minor sparkles-minor-a">
          <path d="M0 -2v4" />
          <path d="M2 0h-4" />
        </g>
      </g>
      <g transform="translate(4 19)">
        <g className="sparkles-minor sparkles-minor-b">
          <path d="M0 -2v4" />
          <path d="M2 0h-4" />
        </g>
      </g>
    </svg>
  )
);

AnimatedSparkles.displayName = "AnimatedSparkles";

function getChipStyle(
  score: number | null,
  isDisplay: boolean,
  isLoading: boolean
) {
  if (score === null) {
    return {
      dot: "bg-muted-foreground/50",
      text: "text-muted-foreground",
      bg: "bg-transparent border-border",
    };
  }
  if (isLoading) {
    return {
      dot: "bg-background",
      text: "text-background",
      bg: "bg-background border-border",
    };
  }
  if (score >= ScoresEnum.strong) {
    return {
      dot: "bg-success-800",
      text: "text-success-800",
      bg: `bg-success-50 border-success-100 ${!isDisplay ? "hover:bg-success-100/70" : ""}`,
    };
  }
  if (score >= ScoresEnum.fair) {
    return {
      dot: "bg-orange-400",
      text: "text-orange-400",
      bg: `bg-orange-50 border-orange-100 ${!isDisplay ? "hover:bg-orange-100/70" : ""}`,
    };
  }
  return {
    dot: "bg-destructive",
    text: "text-destructive",
    bg: `bg-red-50 border-red-100  ${!isDisplay ? "hover:bg-red-100/70" : ""}`,
  };
}

export function HeuristicScoreBadge({
  score,
  isDisplay,
  isLoading,
  isOutdated,
  isError,
  errorMessage,
  onClick,
}: HeuristicScoreBadgeProps) {
  if (isError && !isLoading) {
    return (
      <NoxusTooltip
        asChild
        content={errorMessage ?? "Evaluation failed. Click to retry."}
      >
        <button
          type="button"
          data-testid="prompt-score-badge"
          onClick={onClick}
          className={cn(
            "flex items-center text-xs border transition-all duration-200 ease-out",
            "px-2 py-0.5 rounded-full gap-2 opacity-50",
            "bg-gray-50 border-gray-100 text-gray-500",
            onClick && "cursor-pointer hover:opacity-70"
          )}
        >
          <TriangleAlert size={14} strokeWidth={1.5} className="shrink-0" />
          <span className="font-medium leading-none">Unavailable</span>
        </button>
      </NoxusTooltip>
    );
  }

  const hasScore = typeof score === "number" || isDisplay;
  const showFullScore = isDisplay || isLoading || hasScore;

  const style = isLoading
    ? {
        text: "text-foreground",
        bg: "border-border",
        dot: "bg-muted-foreground/50",
      }
    : isOutdated
      ? {
          text: "text-foreground",
          bg: "border-accent bg-accent",
          dot: "bg-ring/50",
        }
      : showFullScore
        ? getChipStyle(score, !!isDisplay, isLoading)
        : {
            text: "text-foreground",
            bg: "bg-transparent border-transparent",
            dot: "bg-transparent",
          };

  return (
    <NoxusTooltip
      asChild
      content={
        !isDisplay
          ? "Prompt optimizer"
          : isOutdated
            ? "Value is outdated. Run analysis to update."
            : ""
      }
    >
      <button
        type="button"
        data-testid="prompt-score-badge"
        onClick={onClick}
        className={cn(
          // Keep border always (transparent when collapsed) so size doesn't jump
          // and animate padding/radius when switching to loading/score.
          "flex items-center text-xs border border-transparent transition-all duration-200 ease-out",
          "p-1 rounded-md",
          style.bg,
          style.text,
          !showFullScore && "hover:bg-muted",
          showFullScore &&
            "cursor-default px-2.5 py-0.5 rounded-full r gap-1.5",
          onClick && "cursor-pointer",
          isOutdated &&
            hasScore &&
            !isLoading &&
            "opacity-50 border-ring border-dashed"
        )}
      >
        {isLoading ? (
          <Loader2
            size={16}
            className={cn("animate-spin w-4 h-3", style?.text)}
          />
        ) : isDisplay ? (
          <div className={cn("w-2 h-2 rounded-full", style?.dot)} />
        ) : (
          <Sparkles size={16} strokeWidth={1.25} className={style.text} />
        )}

        <span
          className={cn(
            "overflow-hidden whitespace-nowrap transition-[max-width,opacity] duration-200 ease-out",
            isLoading
              ? "font-normal max-w-32 opacity-100"
              : hasScore
                ? "font-medium max-w-32 opacity-100"
                : "max-w-0 opacity-0"
          )}
        >
          {isLoading ? "Analysing" : getScoreLabel(score)}
        </span>
      </button>
    </NoxusTooltip>
  );
}
